import { buttonClass, cx, NavSheet } from "@studyward/ui";
import { BarChart3, CalendarRange, CreditCard, LayoutDashboard, Library, LifeBuoy, ListChecks, LogOut, Menu, Plus, Search, Settings, UploadCloud, Users } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router";
import { signOut } from "../lib/auth";
import { useProfile, useProgrammes, useUsage } from "../lib/queries";
import { AnnouncementBanner } from "./AnnouncementBanner";
import { GlobalHoot } from "./GlobalHoot";
import { DISCLAIMER, OfflineNotice, Wordmark } from "./ui";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/upload", label: "Upload centre", icon: UploadCloud },
  { to: "/sets", label: "My Sets", icon: Library },
  { to: "/practice", label: "Practice", icon: ListChecks },
  { to: "/plan", label: "Study plan", icon: CalendarRange },
  { to: "/progress", label: "Progress", icon: BarChart3 },
  { to: "/groups", label: "Study groups", icon: Users },
  { to: "/billing", label: "Plans & Billing", icon: CreditCard },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/support", label: "Help", icon: LifeBuoy },
] as const;

function getInitials(name?: string | null): string {
  if (!name) return "SW";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "SW";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0] ?? ""}${parts[parts.length - 1]![0] ?? ""}`.toUpperCase();
}

/**
 * Signed-in web app frame (Cupertino Titanium & Optic Blue luxury minimal theme).
 * Desktop: Whisper-quiet hairline left sidebar + light utility bar.
 * Mobile & Tablet: Frosted-glass sticky top header + slide-out responsive menu sheet.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const profile = useProfile();
  const programmes = useProgrammes();
  const usage = useUsage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const programme = programmes.data?.find((p) => p.code === profile.data?.programme);
  const planLabel = usage.data ? (usage.data.plan === "pro" ? "Pro" : "Free plan") : null;
  const onSets = pathname === "/sets";
  const onUpload = pathname === "/upload";
  const initials = getInitials(profile.data?.display_name);

  const navLinks = (
    <nav aria-label="Main" className="flex flex-col gap-1">
      {NAV.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            cx(
              "group flex min-h-10 pointer-coarse:min-h-11 items-center gap-3 rounded-xl px-3.5 py-2 text-small font-medium transition-all",
              isActive
                ? "bg-surface-card font-semibold text-text-primary shadow-card"
                : "text-text-secondary hover:bg-surface-card/60 hover:text-text-primary",
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon size={17} aria-hidden="true" className={cx("shrink-0 transition-colors", isActive ? "text-primary" : "text-text-muted group-hover:text-text-primary")} />
              <span>{label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );

  const account = (
    <div className="flex items-center justify-between gap-2.5 pt-1">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          aria-hidden="true"
          className="grid size-8 shrink-0 place-items-center rounded-full border border-primary/20 bg-primary-soft text-caption font-bold text-text-link"
        >
          {initials}
        </span>
        <div className="min-w-0">
          <p className=" truncate text-small font-semibold leading-tight text-text-primary">
            {profile.data?.display_name || "Your account"}
          </p>
          <p className="truncate text-caption text-text-muted">
            {[programme?.short_name, planLabel].filter(Boolean).join(" · ")}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => void signOut()}
        aria-label="Sign out"
        title="Sign out"
        className="inline-flex min-h-9 pointer-coarse:min-h-11 pointer-coarse:min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-pill px-2.5 text-caption font-medium text-text-secondary transition-colors hover:bg-surface-subtle hover:text-text-primary"
      >
        <LogOut size={15} aria-hidden="true" />
        <span className="lg:sr-only xl:not-sr-only">Sign out</span>
      </button>
    </div>
  );

  return (
    <div className="min-h-dvh bg-surface-background text-text-primary">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-control focus:bg-surface-card focus:p-3 focus:shadow-raised"
      >
        Skip to content
      </a>

      <div className="mx-auto flex min-h-dvh max-w-[1520px] flex-col lg:flex-row">
        {/* Mobile & Tablet Sticky Header */}
        <header className="glass-header sticky top-2 z-20 mx-3 mt-2 flex items-center gap-2 rounded-card py-2 pl-4 pr-2 shadow-card lg:hidden">
          <Link to="/dashboard" aria-label="StudyWard dashboard" className="flex min-h-11 items-center">
            <Wordmark compact />
          </Link>
          {programme && (
            <span className="hidden rounded-md bg-primary-soft px-2 py-0.5 text-caption font-semibold text-text-link sm:inline-block">
              {programme.exam_label}
            </span>
          )}
          {!onUpload && (
            <Link
              to="/upload"
              aria-label="Upload material"
              className="ml-auto grid size-10 shrink-0 pointer-coarse:size-11 place-items-center rounded-pill bg-primary text-text-on-primary shadow-xs hover:bg-primary-pressed"
            >
              <Plus size={19} aria-hidden="true" />
            </Link>
          )}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            className={cx(
              "flex min-h-10 pointer-coarse:min-h-11 items-center gap-2 rounded-pill px-3.5 text-small font-semibold text-text-primary hover:bg-surface-subtle",
              onUpload && "ml-auto",
            )}
          >
            <Menu size={19} aria-hidden="true" />
            Menu
          </button>
        </header>

        {/* Mobile Responsive Slide-Out Navigation Sheet */}
        <NavSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Menu" className="lg:hidden">
          {!onSets && (
            <form
              role="search"
              className="flex items-center gap-2 rounded-control border border-border bg-surface-card px-3.5 py-1.5 shadow-2xs"
              onSubmit={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                navigate(`/sets?q=${encodeURIComponent(query.trim())}`);
              }}
            >
              <Search size={16} aria-hidden="true" className="text-text-muted" />
              <label htmlFor="mobile-global-search" className="sr-only">
                Search your sets
              </label>
              <input
                id="mobile-global-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your sets…"
                className="min-h-9 pointer-coarse:min-h-11 w-full bg-transparent text-small outline-none placeholder:text-text-muted"
              />
            </form>
          )}
          {navLinks}
          <div className="mt-auto border-t border-border pt-4">{account}</div>
        </NavSheet>

        {/* Desktop Whisper-Quiet Hairline Left Sidebar */}
        <aside className="glass-sidebar hidden w-56 shrink-0 flex-col justify-between px-3.5 py-6 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:overflow-y-auto xl:w-60 xl:px-4">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-2 px-2">
              <Link to="/dashboard" aria-label="StudyWard dashboard" className="inline-flex shrink-0 items-center pointer-coarse:min-h-11">
                <Wordmark compact />
              </Link>
              {programme && (
                <Link
                  to="/settings"
                  title="Change your exam in Settings"
                  className="inline-flex items-center rounded-md border border-primary/15 bg-primary-soft px-2 py-0.5 text-caption font-semibold tracking-tight text-text-link transition-colors hover:bg-primary/15 pointer-coarse:min-h-11"
                >
                  {programme.exam_label}
                </Link>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <p className="px-3.5 text-caption font-semibold uppercase tracking-wider text-text-muted">
                Workspace
              </p>
              {navLinks}
            </div>
          </div>

          <div className="mt-6 shrink-0 border-t border-border pt-3">{account}</div>
        </aside>

        {/* Main Content Stream */}
        <div className="flex min-w-0 flex-1 flex-col px-4 py-4 sm:px-6 sm:py-6 lg:px-9 lg:py-7">
          {/* Desktop Light Utility Header (Search + Exam Link + Upload) */}
          <div className="mb-5 hidden flex-wrap items-center justify-end gap-2.5 lg:flex">
            {onSets ? (
              <div className="flex-1" />
            ) : (
              <form
                role="search"
                className="mr-auto flex w-72 items-center gap-2 rounded-control border border-border bg-surface-card px-3.5 py-1 shadow-2xs transition-shadow focus-within:ring-2 focus-within:ring-primary"
                onSubmit={(e) => {
                  e.preventDefault();
                  navigate(`/sets?q=${encodeURIComponent(query.trim())}`);
                }}
              >
                <Search size={16} aria-hidden="true" className="text-text-muted" />
                <label htmlFor="global-search" className="sr-only">
                  Search your sets
                </label>
                <input
                  id="global-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search your sets…"
                  className="min-h-9 pointer-coarse:min-h-11 w-full bg-transparent text-small outline-none placeholder:text-text-muted"
                />
                <kbd className="hidden rounded bg-surface-subtle px-1.5 py-0.5 text-caption font-medium text-text-muted sm:inline-block">
                  ⌘K
                </kbd>
              </form>
            )}
            {!onUpload && (
              <Link to="/upload" className={buttonClass("primary", "sm")}>
                + Upload material
              </Link>
            )}
          </div>

          <OfflineNotice />
          <AnnouncementBanner />

          <main id="main" className="flex-1">
            {children}
          </main>

          <footer className="pb-20 pt-10 text-caption text-text-muted sm:pb-6">{DISCLAIMER}</footer>
        </div>
      </div>

      {/* Persistent Global Hoot (Round Bird Floating Button + Drawer) */}
      <GlobalHoot />
    </div>
  );
}

/** Page heading row: title, optional subtitle and actions. Parents own the spacing below it (flex gap). */
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
      <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
        <h1 className="text-h1 font-semibold tracking-tight text-text-primary">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-small text-text-secondary">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}
